import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';
import { trackDailyLogin } from '../services/gamificationService';
import type { UserBadge } from '../types/gamification';
import { logger } from '../lib/logger';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  isProfileComplete: boolean;
  isAdmin: boolean;
  isSuspended: boolean;
  newlyEarnedBadges: UserBadge[];
  clearNewlyEarnedBadges: () => void;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  reloadProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [newlyEarnedBadges, setNewlyEarnedBadges] = useState<UserBadge[]>([]);

  const clearNewlyEarnedBadges = () => setNewlyEarnedBadges([]);

  useEffect(() => {
    let currentUserId: string | null = null;

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      currentUserId = session?.user?.id ?? null;
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only react to actual auth changes, not token refreshes
      if (event === 'TOKEN_REFRESHED') {
        // Just update session, don't reload profile
        setSession(session);
        return;
      }

      const newUserId = session?.user?.id ?? null;

      // Only reload if user actually changed
      if (newUserId !== currentUserId) {
        currentUserId = newUserId;
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      } else {
        // User same, just update session
        setSession(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);

      // Track daily login and update streak (non-blocking)
      trackDailyLogin(userId)
        .then(({ newBadges }) => {
          if (newBadges.length > 0) {
            setNewlyEarnedBadges((prev) => [...prev, ...newBadges]);
          }
        })
        .catch((err) => {
          // Silently fail - streak tracking is not critical
          logger.warn('Failed to track daily login:', err);
        });
    } catch (error) {
      logger.error('Error loading profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      await loadProfile(user.id);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const isProfileComplete = Boolean(
    profile?.full_name && profile.full_name.trim().length > 0
  );

  const isAdmin = profile?.role === 'admin';
  const isSuspended = profile?.is_suspended === true;

  const reloadProfile = async () => {
    if (user?.id) {
      await loadProfile(user.id);
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    isProfileComplete,
    isAdmin,
    isSuspended,
    newlyEarnedBadges,
    clearNewlyEarnedBadges,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    reloadProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
